import { useCallback, useEffect, useMemo, useState } from 'react';
import { productsApi } from '@/api/products.api';
import type { SelectOption } from '@/components/ui/Select';
import type { ProductResponse } from '@/types/product';
import { formatProductLabel } from '@/utils/entityLabels';
import { useDebounce } from '@/hooks/useDebounce';
import { showApiError } from '@/utils/apiError';

function productOption(p: ProductResponse): SelectOption {
  return { value: String(p.id), label: formatProductLabel(p) };
}

function mergeSelectedOption(options: SelectOption[], selected?: SelectOption | null): SelectOption[] {
  if (!selected) return options;
  if (options.some((opt) => opt.value === selected.value)) return options;
  return [selected, ...options];
}

type Params = { value?: string; pageSize?: number; activeOnly?: boolean };

export function useProductSelectSearch({ value = '', pageSize = 30, activeOnly = true }: Params = {}) {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [products, setProducts] = useState<ProductResponse[]>([]);
  const [selected, setSelected] = useState<ProductResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);

  const load = useCallback(
    async (search: string) => {
      setSearching(true);
      try {
        const res = await productsApi.list({
          search: search || undefined,
          page: 0,
          size: pageSize,
          activeOnly,
        });
        setProducts(res.content);
      } catch (err) {
        showApiError(err, 'Failed to load products');
      } finally {
        setSearching(false);
        setLoading(false);
      }
    },
    [activeOnly, pageSize],
  );

  useEffect(() => {
    void load(debouncedSearch);
  }, [debouncedSearch, load]);

  useEffect(() => {
    if (!value) {
      setSelected(null);
      return;
    }
    const id = Number(value);
    if (Number.isNaN(id)) return;
    const fromList = products.find((p) => p.id === id);
    if (fromList) {
      setSelected(fromList);
      return;
    }
    let cancelled = false;
    productsApi
      .get(id)
      .then((row) => {
        if (!cancelled) setSelected(row);
      })
      .catch(() => {
        if (!cancelled) setSelected(null);
      });
    return () => {
      cancelled = true;
    };
  }, [value, products]);

  const options = useMemo(() => {
    const base = products.map(productOption);
    const sel = selected ? productOption(selected) : null;
    return mergeSelectedOption(base, sel);
  }, [products, selected]);

  const byId = useMemo(() => {
    const map = new Map<number, ProductResponse>();
    for (const p of products) map.set(p.id, p);
    if (selected) map.set(selected.id, selected);
    return map;
  }, [products, selected]);

  const getProduct = useCallback(
    (id: number | string | null | undefined) => {
      if (id == null || id === '') return undefined;
      return byId.get(Number(id));
    },
    [byId],
  );

  return { options, loading, searching, onSearchQuery: setSearchQuery, getProduct };
}
